import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ScrollFadeIn from "./ScrollFadeIn";
import { IconCalendar, IconClock, IconChevronRight } from "@tabler/icons-react";

const RelatedPosts = ({ currentSlug }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelatedPosts = async () => {
      try {
        const response = await fetch(`/api/posts/${currentSlug}/related?limit=3`);
        if (response.ok) {
          const data = await response.json();
          setPosts(data.posts || []);
        }
      } catch (error) {
        console.error("Error fetching related posts:", error);
      } finally {
        setLoading(false);
      }
    };

    if (currentSlug) {
      fetchRelatedPosts();
    }
  }, [currentSlug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getReadingTime = (content) => {
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]+>/g, "");
    const wordCount = text.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  if (loading || posts.length === 0) {
    return null;
  }

  return (
    <section className="bg-[#0B0F11] lg:py-20 py-10">
      <div className="container lg:px-10 px-6 max-w-[1600px] mx-auto">
        <ScrollFadeIn>
          <div className="text-center mb-12">
            <span className="inline-block text-[#fcc171] text-sm font-medium tracking-wider uppercase mb-3">
              Continue Reading
            </span>
            <h2 className="text-white lg:text-3xl text-2xl font-semibold">
              Related Articles
            </h2>
          </div>
        </ScrollFadeIn>

        <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
          {posts.map((post, index) => (
            <ScrollFadeIn key={post.id} delay={index * 0.1}>
              <Link
                to={`/blog/${post.slug}`}
                reloadDocument
                className="group block bg-[#16171D] rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.1)] hover:border-[#C17C56] transition-all duration-300">
                <div className="overflow-hidden h-[180px] bg-gray-800">
                  <img
                    src={
                      post.featuredImage ||
                      "https://placehold.co/600x400/1a1b21/4a4b52?text=No+Image"
                    }
                    alt={post.title.rendered.replace(/<[^>]+>/g, "")}
                    className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-[rgb(171,174,187)] text-xs mb-3">
                    <span className="flex items-center gap-1">
                      <IconCalendar size={14} stroke={1.5} />
                      {formatDate(post.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <IconClock size={14} stroke={1.5} />
                      {getReadingTime(post.content.rendered)} min
                    </span>
                  </div>
                  <h3
                    className="text-white text-lg font-semibold mb-2 line-clamp-2 group-hover:text-[#fcc171] transition-colors duration-300"
                    dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                  />
                  <span className="inline-flex items-center text-[#C17C56] text-sm font-medium group-hover:gap-2 transition-all duration-300">
                    Read Article
                    <IconChevronRight
                      size={16}
                      stroke={2}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    />
                  </span>
                </div>
              </Link>
            </ScrollFadeIn>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedPosts;
